import React, { useEffect, useState, useCallback, useRef } from 'react';
import api from '../api';
import { useAuth } from '../contexts/AuthContext';
import { Team, Player, Game, GameVideo } from '../types';
import { parseGameTime, easternToLocalInput, localInputToEastern } from '../utils/gameTime';
import { mediaUrl } from '../utils/mediaUrl';

// ── Helpers ───────────────────────────────────────────────────────────────────
const RTMP_HOST = 'rtmp://slowrib.com/live';
const HLS_BASE  = process.env.REACT_APP_HLS_URL || 'http://localhost:8000';
const API_URL   = process.env.REACT_APP_API_URL?.replace('/api', '') || '';

const formatDate = (s: string) =>
  parseGameTime(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });

function copyToClipboard(text: string, setCopied: (v: string | null) => void, key: string) {
  navigator.clipboard.writeText(text).then(() => {
    setCopied(key);
    setTimeout(() => setCopied(null), 2000);
  });
}

// ── Stream Info Display ───────────────────────────────────────────────────────
const StreamInfo = ({ streamKey }: { streamKey: string }) => {
  const [copied, setCopied] = useState<string | null>(null);
  const rtmpUrl = RTMP_HOST;

  return (
    <div style={{ background: 'var(--surface-sunken)', border: '1px solid var(--hairline)', borderRadius: '10px', padding: '14px 16px', marginTop: '8px' }}>
      <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '10px' }}>
        📡 Camera / Streaming Info
      </div>
      {[
        { label: 'RTMP URL', value: rtmpUrl, key: 'rtmp' },
        { label: 'Stream Key', value: streamKey, key: 'key' },
      ].map(row => (
        <div key={row.key} style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
          <div style={{ minWidth: '90px', fontSize: '11px', color: 'var(--ink-4)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{row.label}</div>
          <code style={{
            flex: 1, fontSize: '12px', color: 'var(--ink-1)', fontFamily: 'monospace',
            background: 'var(--surface-sunken)', padding: '6px 10px', borderRadius: '6px',
            border: '1px solid var(--hairline)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {row.value}
          </code>
          <button
            onClick={() => copyToClipboard(row.value, setCopied, row.key)}
            style={{
              flexShrink: 0, fontSize: '11px', fontWeight: 700, padding: '5px 10px',
              borderRadius: '6px', cursor: 'pointer',
              background: copied === row.key ? 'rgba(34,197,94,0.15)' : 'rgba(249,115,22,0.1)',
              color: copied === row.key ? '#22C55E' : '#F97316',
              border: `1px solid ${copied === row.key ? 'rgba(34,197,94,0.3)' : 'rgba(249,115,22,0.3)'}`,
              transition: 'all 0.15s',
            }}
          >
            {copied === row.key ? '✓ Copied' : 'Copy'}
          </button>
        </div>
      ))}
    </div>
  );
};

// ── Team Modal ────────────────────────────────────────────────────────────────
const TeamModal = ({
  team, onClose, onSaved,
}: { team?: Team; onClose: () => void; onSaved: (t: Team) => void }) => {
  const [form, setForm] = useState({
    name: team?.name || '',
    sport: team?.sport || 'soccer',
    season_year: team?.season_year?.toString() || new Date().getFullYear().toString(),
    primary_color: team?.primary_color || '#22C55E',
    description: team?.description || '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const set = (f: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
    setForm(p => ({ ...p, [f]: e.target.value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault(); setError(''); setLoading(true);
    try {
      const r = team
        ? await api.put(`/teams/${team.id}`, form)
        : await api.post('/teams', form);
      onSaved(r.data);
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save team');
    } finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{team ? 'Edit Team' : 'New Team'}</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Team Name</label>
            <input className="form-control" value={form.name} onChange={set('name')} required placeholder="e.g. Carmel FC" />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
            <div className="form-group">
              <label>Sport</label>
              <select className="form-control" value={form.sport} onChange={set('sport') as any}>
                <option value="soccer">⚽ Soccer</option>
                <option value="baseball">⚾ Baseball</option>
                <option value="softball">🥎 Softball</option>
                <option value="basketball">🏀 Basketball</option>
              </select>
            </div>
            <div className="form-group">
              <label>Season Year</label>
              <input className="form-control" type="number" value={form.season_year} onChange={set('season_year')} />
            </div>
          </div>
          <div className="form-group">
            <label>Primary Color</label>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <input type="color" value={form.primary_color} onChange={set('primary_color')}
                style={{ width: 44, height: 36, padding: '2px', borderRadius: '6px', border: '1px solid var(--hairline)', background: 'var(--surface-sunken)', cursor: 'pointer' }} />
              <input className="form-control" value={form.primary_color} onChange={set('primary_color')} style={{ flex: 1 }} />
            </div>
          </div>
          <div className="form-group">
            <label>Description (optional)</label>
            <textarea className="form-control" value={form.description} onChange={set('description') as any} rows={2} style={{ resize: 'vertical' }} />
          </div>
          {error && <div style={{ color: '#F87171', fontSize: '13px', marginBottom: '12px' }}>{error}</div>}
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>{loading ? 'Saving...' : team ? 'Save' : 'Create Team'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ── Roster Scan Modal ─────────────────────────────────────────────────────────
interface ScannedPlayer { name: string; number: string; position: string; grade: string; include: boolean; }
const RosterScanModal = ({ teamId, onClose, onImported }: { teamId: string; onClose: () => void; onImported: () => void }) => {
  const [image, setImage]         = useState<File | null>(null);
  const [preview, setPreview]     = useState<string | null>(null);
  const [scanning, setScanning]   = useState(false);
  const [importing, setImporting] = useState(false);
  const [players, setPlayers]     = useState<ScannedPlayer[] | null>(null);
  const [error, setError]         = useState('');
  const [imported, setImported]   = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const pickImage = (file: File) => {
    setImage(file); setPlayers(null); setError('');
    const reader = new FileReader();
    reader.onload = e => setPreview(e.target?.result as string);
    reader.readAsDataURL(file);
  };

  const scan = async () => {
    if (!image) return;
    setScanning(true); setError('');
    try {
      const fd = new FormData(); fd.append('image', image);
      const r = await api.post('/players/roster-scan', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setPlayers((r.data.players as any[]).map(p => ({ ...p, number: p.number ?? '', position: p.position ?? '', grade: p.grade ?? '', include: true })));
    } catch (e: any) { setError(e.response?.data?.error || 'Failed to scan image'); }
    finally { setScanning(false); }
  };

  const update = (i: number, f: keyof ScannedPlayer, v: string | boolean) =>
    setPlayers(ps => ps ? ps.map((p, j) => j === i ? { ...p, [f]: v } : p) : ps);

  const doImport = async () => {
    const toImport = (players || []).filter(p => p.include && p.name.trim());
    if (!toImport.length) { setError('No players selected'); return; }
    setImporting(true); setError('');
    try {
      await api.post('/players/roster-import', { team_id: teamId, players: toImport });
      setImported(true); onImported(); setTimeout(onClose, 1500);
    } catch (e: any) { setError(e.response?.data?.error || 'Import failed'); }
    finally { setImporting(false); }
  };

  const inp: React.CSSProperties = { background: 'var(--surface-sunken)', border: '1px solid var(--hairline)', borderRadius:6,color:'var(--ink-1)',padding:'4px 8px',fontSize:12,width:'100%'};

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 620, maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
        <div className="modal-header"><h2>📷 Scan Roster from Photo</h2><button className="modal-close" onClick={onClose}>✕</button></div>
        <div style={{ overflowY: 'auto', flex: 1 }}>
          {!preview ? (
            <div onClick={() => fileRef.current?.click()} onDragOver={e => e.preventDefault()}
              onDrop={e => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) pickImage(f); }}
              style={{ border:'2px dashed var(--hairline)',borderRadius:12,padding:'48px 24px',textAlign:'center',cursor:'pointer',marginBottom:16 }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>📋</div>
              <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 6 }}>Drop a roster photo here</div>
              <div style={{ fontSize:13,color:'var(--ink-4)',marginBottom:16}}>or click to browse — printed sheets, whiteboards, apps</div>
              <button className="btn btn-secondary btn-sm" type="button">Choose Photo</button>
              <input ref={fileRef} type="file" accept="image/*" capture="environment" style={{ display: 'none' }} onChange={e => { const f = e.target.files?.[0]; if (f) pickImage(f); }} />
            </div>
          ) : (
            <div style={{marginBottom:16}}>
              <img src={preview} alt="Roster" style={{width:'100%',maxHeight:220,objectFit:'contain',borderRadius:10,border:'1px solid var(--hairline)',background:'var(--surface-sunken)'}} />
              <button className="btn btn-ghost btn-sm" style={{marginTop:8}} onClick={()=>{setPreview(null);setImage(null);setPlayers(null);}}>✕ Remove</button>
            </div>
          )}
          {preview && !players && (
            <button className="btn btn-primary" style={{ width: '100%', marginBottom: 16 }} onClick={scan} disabled={scanning}>
              {scanning ? '🔍 Scanning…' : '🔍 Scan Roster with AI'}
            </button>
          )}
          {error && <div style={{ color: '#F87171', fontSize: 13, marginBottom: 12, padding: '8px 12px', background: 'rgba(239,68,68,0.08)', borderRadius: 8 }}>{error}</div>}
          {players && !imported && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                <div style={{fontSize:13,fontWeight:700,color:'var(--ink-3)'}}>Found {players.length} players — edit before importing</div>
                <button className="btn btn-ghost btn-sm" onClick={() => setPlayers(null)}>← Re-scan</button>
              </div>
              <div style={{ display:'grid',gridTemplateColumns:'24px 1fr 60px 100px 70px',gap:6,padding:'6px 4px',fontSize:10,fontWeight:700,color:'var(--ink-4)',textTransform:'uppercase',letterSpacing:'0.6px',borderBottom:'1px solid var(--hairline)',marginBottom:4 }}>
                <div /><div>Name</div><div>#</div><div>Position</div><div>Grade</div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 16 }}>
                {players.map((p, i) => (
                  <div key={i} style={{ display: 'grid', gridTemplateColumns: '24px 1fr 60px 100px 70px', gap: 6, alignItems: 'center', opacity: p.include ? 1 : 0.4 }}>
                    <input type="checkbox" checked={p.include} onChange={e => update(i, 'include', e.target.checked)} style={{ cursor: 'pointer', accentColor: '#F97316' }} />
                    <input value={p.name} onChange={e => update(i, 'name', e.target.value)} style={inp} />
                    <input value={p.number} onChange={e => update(i, 'number', e.target.value)} style={inp} />
                    <input value={p.position} onChange={e => update(i, 'position', e.target.value)} style={inp} />
                    <input value={p.grade} onChange={e => update(i, 'grade', e.target.value)} style={inp} />
                  </div>
                ))}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                <div style={{fontSize:12,color:'var(--ink-4)'}}>{players.filter(p=>p.include).length} of {players.length} selected</div>
                <button className="btn btn-primary" onClick={doImport} disabled={importing}>
                  {importing ? 'Importing…' : `✅ Import ${players.filter(p => p.include).length} Player${players.filter(p => p.include).length !== 1 ? 's' : ''}`}
                </button>
              </div>
            </>
          )}
          {imported && <div style={{ textAlign: 'center', padding: '32px 16px' }}><div style={{ fontSize: 40, marginBottom: 12 }}>✅</div><div style={{ fontWeight: 800, fontSize: 16 }}>Roster imported!</div></div>}
        </div>
      </div>
    </div>
  );
};

// ── Player Modal ──────────────────────────────────────────────────────────────
const PlayerModal = ({
  player, teamId, onClose, onSaved,
}: { player?: Player; teamId: string; onClose: () => void; onSaved: () => void }) => {
  const [form, setForm] = useState({
    name: player?.name || '',
    number: player?.number?.toString() || '',
    position: player?.position || '',
    grade: player?.grade || '',
    is_active: player?.is_active !== 0 ? '1' : '0',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const set = (f: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setForm(p => ({ ...p, [f]: e.target.value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault(); setError(''); setLoading(true);
    try {
      const body = {
        name: form.name,
        number: form.number ? parseInt(form.number) : null,
        position: form.position || null,
        grade: form.grade || null,
        is_active: parseInt(form.is_active),
        team_id: teamId,
      };
      if (player) await api.put(`/players/${player.id}`, body);
      else         await api.post('/players', body);
      onSaved();
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save player');
    } finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: '440px' }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{player ? 'Edit Player' : 'Add Player'}</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Name</label>
            <input className="form-control" value={form.name} onChange={set('name')} required placeholder="Player name" />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
            <div className="form-group">
              <label>Jersey #</label>
              <input className="form-control" type="number" value={form.number} onChange={set('number')} placeholder="e.g. 7" />
            </div>
            <div className="form-group">
              <label>Position</label>
              <input className="form-control" value={form.position} onChange={set('position')} placeholder="e.g. Forward" />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
            <div className="form-group">
              <label>Grade</label>
              <input className="form-control" value={form.grade} onChange={set('grade')} placeholder="e.g. 8th" />
            </div>
            <div className="form-group">
              <label>Status</label>
              <select className="form-control" value={form.is_active} onChange={set('is_active')}>
                <option value="1">Active</option>
                <option value="0">Inactive</option>
              </select>
            </div>
          </div>
          {error && <div style={{ color: '#F87171', fontSize: '13px', marginBottom: '12px' }}>{error}</div>}
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>{loading ? 'Saving...' : player ? 'Save' : 'Add Player'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ── Game Modal ────────────────────────────────────────────────────────────────
const GameModal = ({
  game, teamId, teamName, onClose, onSaved,
}: { game?: Game; teamId: string; teamName: string; onClose: () => void; onSaved: () => void }) => {
  const [form, setForm] = useState({
    title: game?.title || '',
    sport: game?.sport || 'soccer',
    home_team: game?.home_team || teamName,
    away_team: game?.away_team || '',
    scheduled_at: game?.scheduled_at ? easternToLocalInput(game.scheduled_at) : '',
    location: game?.location || '',
    description: game?.description || '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const set = (f: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
    setForm(p => ({ ...p, [f]: e.target.value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault(); setError(''); setLoading(true);
    try {
      const body = { ...form, team_id: teamId, scheduled_at: localInputToEastern(form.scheduled_at) };
      if (game) await api.put(`/games/${game.id}`, body);
      else       await api.post('/games', body);
      onSaved();
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save game');
    } finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{game ? 'Edit Game' : 'New Game'}</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Game Title</label>
            <input className="form-control" value={form.title} onChange={set('title')} required placeholder="e.g. Carmel FC vs. Eagles" />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
            <div className="form-group">
              <label>Sport</label>
              <select className="form-control" value={form.sport} onChange={set('sport') as any}>
                <option value="soccer">⚽ Soccer</option>
                <option value="baseball">⚾ Baseball</option>
                <option value="softball">🥎 Softball</option>
                <option value="basketball">🏀 Basketball</option>
              </select>
            </div>
            <div className="form-group">
              <label>Date & Time</label>
              <input className="form-control" type="datetime-local" value={form.scheduled_at} onChange={set('scheduled_at')} required />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
            <div className="form-group"><label>Away Team</label><input className="form-control" value={form.away_team} onChange={set('away_team')} placeholder="Visiting team" required /></div>
            <div className="form-group"><label>Home Team</label><input className="form-control" value={form.home_team} onChange={set('home_team')} placeholder="Home team" required /></div>
          </div>
          <div className="form-group"><label>Location (optional)</label><input className="form-control" value={form.location} onChange={set('location')} placeholder="Field name or address" /></div>
          <div className="form-group"><label>Notes (optional)</label><textarea className="form-control" value={form.description} onChange={set('description') as any} rows={2} style={{ resize: 'vertical' }} /></div>
          {error && <div style={{ color: '#F87171', fontSize: '13px', marginBottom: '12px' }}>{error}</div>}
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>{loading ? 'Saving...' : game ? 'Save' : 'Create Game'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ── Game Film & Clip Modal (managers + coaches) ───────────────────────────────
interface GameFilmAndClipModalProps {
  game: Game;
  onClose: () => void;
}
const GameFilmAndClipModal = ({ game, onClose }: GameFilmAndClipModalProps) => {
  const videoRef  = useRef<HTMLVideoElement>(null);
  const [recording, setRecording]   = useState<GameVideo | null | undefined>(undefined);
  const [clips,     setClips]       = useState<GameVideo[]>([]);
  const [players,   setPlayers]     = useState<Player[]>([]);
  const [tab,       setTab]         = useState<'film' | 'upload'>('film');
  // Clipper
  const [showClipper, setShowClipper] = useState(false);
  const [clipIn,    setClipIn]      = useState<number | null>(null);
  const [clipOut,   setClipOut]     = useState<number | null>(null);
  const [vidDur,    setVidDur]      = useState(0);
  const [clipTitle, setClipTitle]   = useState('');
  const [taggedIds, setTaggedIds]   = useState<string[]>([]);
  const [clipping,  setClipping]    = useState(false);
  const [clipErr,   setClipErr]     = useState('');
  const [clipDone,  setClipDone]    = useState(false);
  // Upload
  const [hlFile,    setHlFile]      = useState<File | null>(null);
  const [hlTitle,   setHlTitle]     = useState('');
  const [hlProg,    setHlProg]      = useState(0);
  const [hlLoading, setHlLoading]   = useState(false);
  const [hlErr,     setHlErr]       = useState('');

  const loadData = useCallback(() => {
    api.get(`/videos?game_id=${game.id}`).then(r => {
      const vids = r.data as GameVideo[];
      setRecording(vids.find(v => v.video_type === 'recording') ?? null);
      setClips(vids.filter(v => v.video_type === 'highlight').sort((a, b) =>
        new Date(b.uploaded_at || 0).getTime() - new Date(a.uploaded_at || 0).getTime()
      ));
    }).catch(() => setRecording(null));
    if (game.team_id) api.get(`/players?team_id=${game.team_id}`).then(r => setPlayers(r.data)).catch(() => {});
  }, [game.id, game.team_id]);

  useEffect(() => { loadData(); }, [loadData]);

  const recUrl = recording
    ? mediaUrl(recording.file_path.startsWith('http') || recording.file_path.startsWith('/api/')
        ? recording.file_path
        : recording.file_path.startsWith('/uploads/')
          ? `${API_URL}${recording.file_path}`
          : `${HLS_BASE}${recording.file_path}`)
    : null;

  const fmt = (s: number) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;
  const markIn  = () => { if (videoRef.current) setClipIn(videoRef.current.currentTime); };
  const markOut = () => { if (videoRef.current) setClipOut(videoRef.current.currentTime); };
  const togglePlayer = (id: string) => setTaggedIds(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);

  const saveClip = async () => {
    if (clipIn === null || clipOut === null) { setClipErr('Set both start and end points'); return; }
    if (clipOut <= clipIn) { setClipErr('End must be after start'); return; }
    if (!recording) return;
    setClipping(true); setClipErr('');
    try {
      const r = await api.post(`/videos/${recording.id}/clip`, {
        start_seconds: clipIn, end_seconds: clipOut,
        title: clipTitle, player_ids: taggedIds,
      });
      setClips(prev => [...(r.data.clips as GameVideo[]), ...prev]);
      setClipIn(null); setClipOut(null); setClipTitle(''); setTaggedIds([]);
      setShowClipper(false); setClipDone(true);
      setTimeout(() => setClipDone(false), 3000);
    } catch (e: any) { setClipErr(e.response?.data?.error || 'Clip creation failed'); }
    finally { setClipping(false); }
  };

  const uploadHighlight = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!hlFile) { setHlErr('Select a video file'); return; }
    setHlLoading(true); setHlErr('');
    try {
      const fd = new FormData();
      fd.append('video', hlFile);
      fd.append('game_id', game.id);
      if (hlTitle.trim()) fd.append('title', hlTitle.trim());
      await api.post('/videos/upload-game-highlight', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (ev: any) => setHlProg(Math.round((ev.loaded / (ev.total || 1)) * 100)),
      });
      loadData(); setTab('film'); setHlFile(null); setHlTitle('');
    } catch (e: any) { setHlErr(e.response?.data?.error || 'Upload failed'); }
    finally { setHlLoading(false); }
  };

  const inPct  = vidDur > 0 && clipIn  !== null ? (clipIn  / vidDur) * 100 : null;
  const outPct = vidDur > 0 && clipOut !== null ? (clipOut / vidDur) * 100 : null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 860, maxHeight: '95vh', overflowY: 'auto', padding: 0 }}>

        {/* Header */}
        <div style={{ padding: '20px 24px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div style={{fontWeight:800,fontSize:16,color:'var(--ink-1)'}}>{game.title}</div>
            <div style={{fontSize:12,color:'var(--ink-3)'}}>{game.away_team} @ {game.home_team}</div>
          </div>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        {/* Tabs */}
        <div style={{display:'flex',borderBottom:'1px solid var(--hairline)',margin:'16px 0 0',padding:'0 24px'}}>
          {(['film', 'upload'] as const).map(t => (
            <button key={t} onClick={() => setTab(t)} style={{padding:'8px 16px',background:'none',border:'none',borderBottom:`2px solid ${tab===t?'#F97316':'transparent'}`,color:tab===t?'#F97316':'var(--ink-3)',fontWeight:tab===t?700:400,fontSize:13,cursor:'pointer',marginBottom:-1}}>
              {t === 'film' ? '🎥 Film & Clips' : '📤 Upload Reel'}
            </button>
          ))}
        </div>

        <div style={{ padding: '16px 24px 24px' }}>
          {tab === 'film' ? (
            <>
              {/* Recording */}
              {recording === undefined ? (
                <div style={{padding:40,textAlign:'center',color:'var(--ink-3)'}}>Loading…</div>
              ) : recUrl ? (
                <video ref={videoRef} src={recUrl} controls playsInline
                  style={{ width: '100%', borderRadius: 10, background: '#000', maxHeight: '45vh', display: 'block', marginBottom: 12 }}
                  onDurationChange={e => setVidDur((e.target as HTMLVideoElement).duration)} />
              ) : (
                <div style={{background:'var(--surface-sunken)',border:'1px solid var(--hairline)',borderRadius:10,padding:'32px',textAlign:'center',color:'var(--ink-4)',marginBottom:12}}>
                  <div style={{fontSize:36,marginBottom:8}}>🎬</div>
                  <div>No game recording found.</div>
                </div>
              )}

              {/* Create clip toggle */}
              {recUrl && (
                <div style={{marginBottom:12}}>
                  <div style={{display:'flex',gap:8,alignItems:'center'}}>
                    <button onClick={() => setShowClipper(v => !v)} style={{padding:'7px 16px',borderRadius:8,border:`1px solid ${showClipper?'#F97316':'var(--hairline)'}`,background:showClipper?'rgba(249,115,22,0.1)':'transparent',color:showClipper?'#F97316':'var(--ink-3)',fontWeight:700,fontSize:12,cursor:'pointer'}}>
                      ✂️ {showClipper ? 'Hide Clip Creator' : 'Create Highlight Clip'}
                    </button>
                    {clipDone && <span style={{ fontSize: 12, color: '#22C55E' }}>✅ Clip saved to player profiles!</span>}
                  </div>
                </div>
              )}

              {/* Clipper panel */}
              {showClipper && recUrl && (
                <div style={{background:'var(--surface-sunken)',border:'1px solid rgba(249,115,22,0.25)',borderRadius:10,padding:16,marginBottom:12}}>
                  <div style={{fontWeight:700,fontSize:12,color:'#F97316',marginBottom:12,textTransform:'uppercase',letterSpacing:'0.6px'}}>✂️ Clip Creator — play to a moment then mark start/end</div>
                  <div style={{display:'flex',gap:8,marginBottom:12,flexWrap:'wrap'}}>
                    <button onClick={markIn} style={{padding:'8px 14px',borderRadius:8,border:`2px solid ${clipIn!==null?'#F97316':'var(--hairline)'}`,background:clipIn!==null?'rgba(249,115,22,0.12)':'var(--surface)',color:clipIn!==null?'#F97316':'var(--ink-3)',fontWeight:700,fontSize:12,cursor:'pointer'}}>
                      ⏮ Set Start {clipIn !== null ? `(${fmt(clipIn)})` : ''}
                    </button>
                    <button onClick={markOut} style={{padding:'8px 14px',borderRadius:8,border:`2px solid ${clipOut!==null?'#22C55E':'var(--hairline)'}`,background:clipOut!==null?'rgba(34,197,94,0.12)':'var(--surface)',color:clipOut!==null?'#22C55E':'var(--ink-3)',fontWeight:700,fontSize:12,cursor:'pointer'}}>
                      Set End ⏭ {clipOut !== null ? `(${fmt(clipOut)})` : ''}
                    </button>
                    {clipIn !== null && clipOut !== null && clipOut > clipIn && (
                      <span style={{alignSelf:'center',fontSize:12,color:'var(--ink-3)'}}>Duration: <b style={{color:'var(--ink-1)'}}>{fmt(clipOut - clipIn)}</b></span>
                    )}
                  </div>
                  {vidDur > 0 && (
                    <div style={{position:'relative',height:20,background:'var(--surface)',border:'1px solid var(--hairline)',borderRadius:4,marginBottom:12,overflow:'hidden'}}>
                      {inPct !== null && outPct !== null && <div style={{ position: 'absolute', left: `${inPct}%`, width: `${Math.max(outPct - inPct, 0)}%`, height: '100%', background: 'rgba(249,115,22,0.4)', borderLeft: '2px solid #F97316', borderRight: '2px solid #22C55E' }} />}
                      <div style={{position:'absolute',right:4,bottom:1,fontSize:9,color:'var(--ink-4)'}}>{fmt(vidDur)}</div>
                    </div>
                  )}
                  <input className="form-control" value={clipTitle} onChange={e => setClipTitle(e.target.value)} placeholder="Clip title (e.g. Great save, Corner kick goal)" style={{ fontSize: 12, marginBottom: 10 }} />
                  {players.length > 0 && (
                    <div style={{ marginBottom: 10 }}>
                      <div style={{fontSize:11,fontWeight:700,color:'var(--ink-3)',textTransform:'uppercase',letterSpacing:'0.5px',marginBottom:6}}>Tag Players</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {players.map(p => {
                          const sel = taggedIds.includes(p.id);
                          return (
                            <label key={p.id} style={{display:'flex',alignItems:'center',gap:6,padding:'4px 10px',borderRadius:6,cursor:'pointer',background:sel?'rgba(249,115,22,0.1)':'var(--surface)',border:`1px solid ${sel?'#F97316':'var(--hairline)'}`,fontSize:12}}>
                              <input type="checkbox" checked={sel} onChange={() => togglePlayer(p.id)} style={{ accentColor: '#F97316' }} />
                              #{p.number ?? '?'} {p.name}
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  {clipErr && <div style={{ color: '#F87171', fontSize: 12, marginBottom: 8 }}>{clipErr}</div>}
                  <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                    <button onClick={() => { setShowClipper(false); setClipIn(null); setClipOut(null); setClipErr(''); }} style={{padding:'7px 14px',borderRadius:7,border:'1px solid var(--hairline)',background:'transparent',color:'var(--ink-3)',fontSize:12,cursor:'pointer'}}>Cancel</button>
                    <button onClick={saveClip} disabled={clipping || clipIn === null || clipOut === null}
                      style={{padding:'7px 18px',borderRadius:7,border:'none',background:clipping?'var(--hairline)':'#F97316',color:clipping?'var(--ink-4)':'#fff',fontWeight:700,fontSize:12,cursor:clipping?'default':'pointer'}}>
                      {clipping ? '⏳ Creating…' : '💾 Save Clip'}
                    </button>
                  </div>
                  {clipping && <div style={{fontSize:11,color:'var(--ink-3)',textAlign:'center',marginTop:6}}>This may take 30–60 seconds…</div>}
                </div>
              )}

              {/* Clips list */}
              {clips.length > 0 && (
                <div>
                  <div style={{fontSize:11,fontWeight:700,color:'var(--ink-3)',textTransform:'uppercase',letterSpacing:'0.6px',marginBottom:8}}>Highlights & Clips ({clips.length})</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {clips.map(clip => {
                      const player = clip.player_id ? players.find(p => p.id === clip.player_id) : null;
                      const clipUrl = mediaUrl(clip.file_path.startsWith('http') || clip.file_path.startsWith('/api/') ? clip.file_path : clip.file_path.startsWith('/uploads/') ? `${API_URL}${clip.file_path}` : `${HLS_BASE}${clip.file_path}`);
                      return (
                        <div key={clip.id} style={{display:'flex',alignItems:'center',gap:10,padding:'8px 12px',background:'var(--surface-sunken)',borderRadius:8,border:'1px solid var(--hairline)'}}>
                          <div style={{fontSize:20}}>🎞️</div>
                          <div style={{flex:1,minWidth:0}}>
                            <div style={{fontWeight:700,fontSize:13,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{clip.title || 'Highlight'}</div>
                            {player && <div style={{fontSize:11,color:'var(--ink-3)'}}>#{player.number} {player.name}</div>}
                            {clip.duration_seconds && <div style={{fontSize:11,color:'var(--ink-4)'}}>{fmt(clip.duration_seconds)}</div>}
                          </div>
                          <a href={clipUrl} target="_blank" rel="noreferrer" style={{ padding: '4px 10px', borderRadius: 6, background: 'rgba(249,115,22,0.1)', border: '1px solid rgba(249,115,22,0.3)', color: '#F97316', fontSize: 12, fontWeight: 700, textDecoration: 'none' }}>▶</a>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          ) : (
            /* Upload tab */
            <form onSubmit={uploadHighlight}>
              <div style={{fontSize:13,color:'var(--ink-3)',marginBottom:16}}>Upload a pre-edited highlight reel video for this game.</div>
              <div className="form-group">
                <label>Title (optional)</label>
                <input className="form-control" value={hlTitle} onChange={e => setHlTitle(e.target.value)} placeholder="e.g. Game Highlights, Best Plays…" />
              </div>
              <div className="form-group">
                <label>Video File</label>
                <input type="file" accept="video/*" className="form-control" style={{ padding: 8 }} onChange={e => setHlFile(e.target.files?.[0] || null)} />
                {hlFile && <div style={{fontSize:11,color:'var(--ink-3)',marginTop:4}}>{hlFile.name} · {(hlFile.size/1024/1024).toFixed(1)} MB</div>}
              </div>
              {hlLoading && (
                <div style={{ marginBottom: 12 }}>
                  <div style={{display:'flex',justifyContent:'space-between',fontSize:12,color:'var(--ink-3)',marginBottom:4}}><span>Uploading…</span><span>{hlProg}%</span></div>
                  <div style={{height:6,background:'var(--hairline)',borderRadius:4}}>
                    <div style={{ height: '100%', width: `${hlProg}%`, background: '#22C55E', borderRadius: 4, transition: 'width 0.2s' }} />
                  </div>
                </div>
              )}
              {hlErr && <div style={{ color: '#F87171', fontSize: 13, marginBottom: 10 }}>{hlErr}</div>}
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button type="button" className="btn btn-secondary" onClick={() => setTab('film')} disabled={hlLoading}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={hlLoading || !hlFile}>{hlLoading ? 'Uploading…' : '⬆️ Upload'}</button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Main ManagerPortal ────────────────────────────────────────────────────────
const ManagerPortal = () => {
  const { managedTeamIds, isAdmin } = useAuth();
  const [activeTab, setActiveTab] = useState<'teams' | 'roster' | 'schedule' | 'users'>('teams');
  const [teams, setTeams] = useState<Team[]>([]);
  const [allTeams, setAllTeams] = useState<Team[]>([]); // admin sees all
  const [players, setPlayers] = useState<Player[]>([]);
  const [games, setGames] = useState<Game[]>([]);
  const [pendingUsers, setPendingUsers] = useState<any[]>([]);
  const [allPlayers, setAllPlayers] = useState<Player[]>([]); // for linking P/P
  const [selectedTeamId, setSelectedTeamId] = useState<string>('');
  const [toast, setToast] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Modals
  const [teamModal, setTeamModal]     = useState<{ open: boolean; team?: Team }>({ open: false });
  const [playerModal, setPlayerModal]     = useState<{ open: boolean; player?: Player }>({ open: false });
  const [rosterScanOpen, setRosterScanOpen]       = useState(false);
  const [clipperGame, setClipperGame]             = useState<Game | null>(null);
  const [gameModal, setGameModal]     = useState<{ open: boolean; game?: Game }>({ open: false });

  // Expanded stream info
  const [expandedStream, setExpandedStream] = useState<string | null>(null);

  // iCal state
  const [icalUrl, setIcalUrl]           = useState('');
  const [icalSaving, setIcalSaving]     = useState(false);
  const [icalSyncing, setIcalSyncing]   = useState(false);
  const [icalResult, setIcalResult]     = useState('');

  // P/P player link state
  const [approvePlayerLinks, setApprovePlayerLinks] = useState<Record<string, string>>({});

  const showToast = useCallback((msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  }, []);

  const fetchMyTeams = useCallback(async () => {
    try {
      const r = await api.get('/teams');
      const all = r.data as Team[];
      if (isAdmin) {
        setAllTeams(all);
        setTeams(all);
      } else {
        const mine = all.filter((t: Team) => managedTeamIds.includes(t.id));
        setAllTeams(all);
        setTeams(mine);
      }
    } catch {}
  }, [managedTeamIds, isAdmin]);

  const fetchPlayers = useCallback(async () => {
    if (!selectedTeamId) return;
    try {
      const r = await api.get(`/players?team_id=${selectedTeamId}`);
      setPlayers(r.data);
    } catch {}
  }, [selectedTeamId]);

  const fetchGames = useCallback(async () => {
    if (!selectedTeamId) return;
    try {
      const r = await api.get('/games');
      setGames((r.data as Game[]).filter(g => g.team_id === selectedTeamId));
    } catch {}
  }, [selectedTeamId]);

  const fetchPendingUsers = useCallback(async () => {
    try {
      const r = await api.get('/auth/manager/pending-users');
      setPendingUsers(r.data);
    } catch {}
  }, []);

  const fetchAllPlayers = useCallback(async () => {
    try {
      const r = await api.get('/players');
      setAllPlayers(r.data);
    } catch {}
  }, []);

  useEffect(() => {
    fetchMyTeams().then(() => setLoading(false));
    fetchPendingUsers();
    fetchAllPlayers();
  }, [fetchMyTeams, fetchPendingUsers, fetchAllPlayers]);

  // Auto-select first managed team when teams load
  useEffect(() => {
    if (teams.length > 0 && !selectedTeamId) {
      setSelectedTeamId(teams[0].id);
    }
  }, [teams, selectedTeamId]);

  useEffect(() => {
    if (selectedTeamId) {
      fetchPlayers();
      fetchGames();
    }
  }, [selectedTeamId, fetchPlayers, fetchGames]);

  const selectedTeam = teams.find(t => t.id === selectedTeamId) || allTeams.find(t => t.id === selectedTeamId);

  // Keep icalUrl input in sync when selected team changes
  useEffect(() => {
    setIcalUrl(selectedTeam?.ical_url || '');
    setIcalResult('');
  }, [selectedTeam?.ical_url, selectedTeamId]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveIcalUrl = async () => {
    if (!selectedTeamId) return;
    setIcalSaving(true); setIcalResult('');
    try {
      await api.put(`/teams/${selectedTeamId}/ical`, { ical_url: icalUrl.trim() });
      await fetchMyTeams();
      setIcalResult(icalUrl.trim()
        ? '✓ URL saved — schedule syncs automatically every hour'
        : '✓ iCal removed');
      setTimeout(() => setIcalResult(''), 5000);
    } catch (err: any) {
      setIcalResult(`✗ ${err.response?.data?.error || 'Failed to save URL'}`);
      setTimeout(() => setIcalResult(''), 5000);
    } finally { setIcalSaving(false); }
  };

  const syncIcalNow = async () => {
    if (!selectedTeamId) return;
    setIcalSyncing(true); setIcalResult('');
    try {
      const r = await api.post(`/teams/${selectedTeamId}/sync-ical`);
      const { imported, skipped } = r.data;
      setIcalResult(imported > 0
        ? `✓ +${imported} new game${imported !== 1 ? 's' : ''} imported`
        : `✓ Up to date (${skipped} already exist)`);
      await fetchMyTeams();
      if (imported > 0) fetchGames();
      setTimeout(() => setIcalResult(''), 5000);
    } catch (err: any) {
      setIcalResult(`✗ ${err.response?.data?.error || 'Sync failed'}`);
      setTimeout(() => setIcalResult(''), 5000);
    } finally { setIcalSyncing(false); }
  };

  const handleApprovePendingUser = async (userId: string) => {
    const u = pendingUsers.find(p => p.id === userId);
    if (!u) return;
    const linkedPlayerId = u.requested_role === 'player_parent' ? approvePlayerLinks[userId] : undefined;
    if (u.requested_role === 'player_parent' && !linkedPlayerId) {
      alert('Please select a player to link before approving a Player/Parent.');
      return;
    }
    const teamId = teams[0]?.id || '';
    try {
      await api.post(`/auth/users/${userId}/approve-for-team`, {
        linked_player_id: linkedPlayerId || null,
        team_id: teamId,
      });
      await fetchPendingUsers();
      showToast('User approved');
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to approve user');
    }
  };

  const handleRejectPendingUser = async (userId: string) => {
    if (!window.confirm('Reject this account request?')) return;
    try {
      await api.post(`/auth/users/${userId}/reject`);
      await fetchPendingUsers();
      showToast('User rejected');
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to reject user');
    }
  };

  const deletePlayer = async (playerId: string) => {
    if (!window.confirm('Remove this player? This cannot be undone.')) return;
    try {
      await api.delete(`/players/${playerId}`);
      await fetchPlayers();
      showToast('Player removed');
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to remove player');
    }
  };

  const deleteGame = async (gameId: string) => {
    if (!window.confirm('Delete this game? This cannot be undone.')) return;
    try {
      await api.delete(`/games/${gameId}`);
      await fetchGames();
      showToast('Game deleted');
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to delete game');
    }
  };

  const TabBtn = ({ id, label }: { id: typeof activeTab; label: string }) => (
    <button onClick={() => setActiveTab(id)} style={{
      padding:'9px 18px',border:'none',borderRadius:'8px',fontWeight:600,fontSize:'14px',
      cursor:'pointer',transition:'all 0.15s',
      background:activeTab===id?'rgba(34,197,94,0.15)':'transparent',
      color:activeTab===id?'#22C55E':'var(--ink-3)',
    }}>{label}</button>
  );

  // Team selector (shared between Roster, Schedule tabs)
  const TeamSelector = () => (
    <div className="form-group" style={{ marginBottom: '20px', maxWidth: '320px' }}>
      <label style={{fontSize:'11px',color:'var(--ink-3)',fontWeight:700,textTransform:'uppercase',letterSpacing:'0.5px'}}>Team</label>
      <select
        className="form-control"
        value={selectedTeamId}
        onChange={e => setSelectedTeamId(e.target.value)}
      >
        {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
      </select>
    </div>
  );

  if (loading) {
    return (
      <div className="container" style={{textAlign:'center',padding:'80px',color:'var(--ink-4)'}}>
        Loading...
      </div>
    );
  }

  return (
    <div className="container">
      {/* Header */}
      <div style={{ marginBottom: '28px' }}>
        <h1 style={{ fontSize: '30px', fontWeight: 900, letterSpacing: '-0.5px', marginBottom: '4px' }}>
          🗂️ Manager Portal
        </h1>
        <p style={{color:'var(--ink-3)',fontSize:'14px'}}>
          Manage your team{teams.length !== 1 ? 's' : ''}, roster, schedule, and users.
        </p>
      </div>

      {/* Tabs */}
      <div style={{display:'flex',gap:'4px',marginBottom:'20px',borderBottom:'1px solid var(--hairline)',paddingBottom:'4px'}}>
        <TabBtn id="teams"    label="🏆 My Teams" />
        <TabBtn id="roster"   label="👥 Roster" />
        <TabBtn id="schedule" label="📅 Schedule" />
        <TabBtn id="users"    label={`👤 Users${pendingUsers.length > 0 ? ` (${pendingUsers.length})` : ''}`} />
      </div>

      {/* ── Tab: My Teams ── */}
      {activeTab === 'teams' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px' }}>
            <button className="btn btn-primary" onClick={() => setTeamModal({ open: true })}>
              ➕ Add Team
            </button>
          </div>

          {teams.length === 0 ? (
            <div style={{textAlign:'center',padding:'80px 20px',background:'var(--surface)',border:'1px solid var(--hairline)',borderRadius:'16px'}}>
              <div style={{fontSize:'56px',marginBottom:'16px'}}>🏆</div>
              <h3 style={{fontSize:'20px',fontWeight:800,marginBottom:'8px'}}>No teams yet</h3>
              <p style={{color:'var(--ink-3)',fontSize:'14px',marginBottom:'20px'}}>
                Create your first team to get started.
              </p>
              <button className="btn btn-primary" onClick={() => setTeamModal({ open: true })}>+ Create Team</button>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {teams.map(team => (
                <div key={team.id} style={{
                  background:'var(--surface)',border:'1px solid var(--hairline)',borderRadius:'14px',
                  padding:'20px',display:'flex',alignItems:'center',gap:'16px',flexWrap:'wrap',
                }}>
                  {/* Color dot */}
                  <div style={{
                    width:44,height:44,borderRadius:'10px',flexShrink:0,
                    background:team.primary_color,display:'flex',alignItems:'center',
                    justifyContent:'center',fontSize:'22px',
                  }}>
                    {team.sport === 'soccer' ? '⚽' : team.sport === 'baseball' ? '⚾' : team.sport === 'basketball' ? '🏀' : '🥎'}
                  </div>

                  <div style={{flex:1,minWidth:'160px'}}>
                    <div style={{fontWeight:800,fontSize:'16px',marginBottom:'3px'}}>{team.name}</div>
                    <div style={{fontSize:'13px',color:'var(--ink-3)'}}>
                      {team.sport.charAt(0).toUpperCase() + team.sport.slice(1)} · {team.season_year}
                    </div>
                    {team.record && (
                      <div style={{fontSize:'12px',color:'var(--ink-4)',marginTop:'3px'}}>
                        {team.record.wins}W–{team.record.losses}L{team.record.ties > 0 ? `–${team.record.ties}T` : ''}
                      </div>
                    )}
                  </div>

                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={() => { setSelectedTeamId(team.id); setActiveTab('roster'); }}
                    >
                      👥 Roster
                    </button>
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={() => { setSelectedTeamId(team.id); setActiveTab('schedule'); }}
                    >
                      📅 Schedule
                    </button>
                    <button
                      className="btn btn-sm"
                      style={{ background: 'rgba(34,197,94,0.1)', color: '#22C55E', border: '1px solid rgba(34,197,94,0.3)' }}
                      onClick={() => setTeamModal({ open: true, team })}
                    >
                      ✏️ Edit
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Tab: Roster ── */}
      {activeTab === 'roster' && (
        <div>
          {teams.length === 0 ? (
            <div style={{textAlign:'center',padding:'60px',color:'var(--ink-4)'}}>
              No managed teams — create one first.
            </div>
          ) : (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
                <TeamSelector />
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="btn btn-secondary" onClick={() => setRosterScanOpen(true)}>📷 Scan Roster</button>
                  <button className="btn btn-primary" onClick={() => setPlayerModal({ open: true })}>➕ Add Player</button>
                </div>
              </div>

              {players.length === 0 ? (
                <div style={{textAlign:'center',padding:'60px',background:'var(--surface)',border:'1px solid var(--hairline)',borderRadius:'16px'}}>
                  <div style={{fontSize:'40px',marginBottom:'12px'}}>👥</div>
                  <p style={{color:'var(--ink-3)'}}>No players on this roster yet.</p>
                  <button className="btn btn-primary" style={{ marginTop: '12px' }} onClick={() => setPlayerModal({ open: true })}>
                    + Add First Player
                  </button>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {players.map(player => (
                    <div key={player.id} style={{
                      background:'var(--surface)',border:'1px solid var(--hairline)',borderRadius:'12px',
                      padding:'14px 18px',display:'flex',alignItems:'center',gap:'14px',flexWrap:'wrap',
                    }}>
                      {/* Photo or avatar */}
                      <div style={{
                        width:40,height:40,borderRadius:'50%',flexShrink:0,overflow:'hidden',
                        background:'linear-gradient(135deg, #F97316, #EA580C)',
                        display:'flex',alignItems:'center',justifyContent:'center',
                        fontSize:'14px',fontWeight:800,color:'#fff',
                      }}>
                        {player.photo_url ? (
                          <img
                            src={mediaUrl(player.photo_url)}
                            alt={player.name}
                            style={{width:'100%',height:'100%',objectFit:'cover'}}
                          />
                        ) : (player.name.charAt(0).toUpperCase())}
                      </div>

                      <div style={{flex:1,minWidth:'140px'}}>
                        <div style={{fontWeight:700,fontSize:'15px'}}>
                          {player.name}
                          {player.number && <span style={{color:'#F97316',marginLeft:'8px',fontSize:'13px'}}>#{player.number}</span>}
                        </div>
                        <div style={{fontSize:'12px',color:'var(--ink-3)',marginTop:'2px'}}>
                          {[player.position, player.grade].filter(Boolean).join(' · ') || 'No details'}
                        </div>
                      </div>

                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                          className="btn btn-sm"
                          style={{ background: 'rgba(34,197,94,0.1)', color: '#22C55E', border: '1px solid rgba(34,197,94,0.3)' }}
                          onClick={() => setPlayerModal({ open: true, player })}
                        >
                          ✏️ Edit
                        </button>
                        <button className="btn btn-danger btn-sm" onClick={() => deletePlayer(player.id)}>
                          Remove
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ── Tab: Schedule ── */}
      {activeTab === 'schedule' && (
        <div>
          {teams.length === 0 ? (
            <div style={{textAlign:'center',padding:'60px',color:'var(--ink-4)'}}>
              No managed teams — create one first.
            </div>
          ) : (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
                <TeamSelector />
                <button className="btn btn-primary" onClick={() => setGameModal({ open: true })}>
                  ➕ Add Game
                </button>
              </div>

              {/* ── iCal auto-sync panel ── */}
              <div style={{background:'var(--surface)',border:'1px solid var(--hairline)',borderRadius:'12px',padding:'16px 20px',marginBottom:'20px'}}>
                <div style={{display:'flex',alignItems:'center',gap:'10px',marginBottom:'10px'}}>
                  <span style={{fontSize:'18px'}}>📅</span>
                  <div style={{fontWeight:700,fontSize:'14px'}}>Auto-Sync from iCal</div>
                  {selectedTeam?.ical_url ? (
                    <span style={{fontSize:'11px',fontWeight:700,color:'#22C55E',background:'rgba(34,197,94,0.1)',border:'1px solid rgba(34,197,94,0.2)',borderRadius:'20px',padding:'2px 8px'}}>
                      Active
                    </span>
                  ) : (
                    <span style={{fontSize:'11px',color:'var(--ink-4)',background:'rgba(74,85,104,0.15)',border:'1px solid var(--hairline)',borderRadius:'20px',padding:'2px 8px'}}>
                      Not set
                    </span>
                  )}
                </div>

                {selectedTeam?.ical_last_synced_at && (
                  <div style={{fontSize:'12px',color:'var(--ink-4)',marginBottom:'8px'}}>
                    Last synced: {new Date(selectedTeam.ical_last_synced_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                  </div>
                )}

                <div style={{fontSize:'12px',color:'var(--ink-3)',marginBottom:'12px'}}>
                  Paste an .ics subscription link from TeamSnap, GameChanger, Stack TeamApp, or any calendar app. Games sync automatically every hour. Clear the field and save to remove.
                </div>

                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <input
                    className="form-control"
                    style={{ flex: 1, minWidth: '200px', fontSize: '13px' }}
                    value={icalUrl}
                    onChange={e => setIcalUrl(e.target.value)}
                    placeholder="https://… or webcal://…"
                  />
                  <button
                    className="btn btn-primary"
                    onClick={saveIcalUrl}
                    disabled={icalSaving}
                    style={{ flexShrink: 0 }}
                  >
                    {icalSaving ? 'Saving…' : icalUrl.trim() ? 'Save URL' : 'Remove iCal'}
                  </button>
                  {selectedTeam?.ical_url && (
                    <button
                      className="btn btn-secondary"
                      onClick={syncIcalNow}
                      disabled={icalSyncing}
                      style={{ flexShrink: 0 }}
                    >
                      {icalSyncing ? '⟳ Syncing…' : '🔄 Sync Now'}
                    </button>
                  )}
                </div>

                {icalResult && (
                  <div style={{ marginTop: '10px', fontSize: '13px', color: icalResult.startsWith('✓') ? '#22C55E' : '#F87171' }}>
                    {icalResult}
                  </div>
                )}
              </div>

              {games.length === 0 ? (
                <div style={{textAlign:'center',padding:'60px',background:'var(--surface)',border:'1px solid var(--hairline)',borderRadius:'16px'}}>
                  <div style={{fontSize:'40px',marginBottom:'12px'}}>📅</div>
                  <p style={{color:'var(--ink-3)'}}>No games scheduled yet.</p>
                  <button className="btn btn-primary" style={{marginTop:'12px'}} onClick={() => setGameModal({ open: true })}>
                    + Schedule First Game
                  </button>
                </div>
              ) : (
                <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
                  {[...games].sort((a, b) => new Date(a.scheduled_at).getTime() - new Date(b.scheduled_at).getTime()).map(game => (
                    <div key={game.id} style={{
                      background:'var(--surface)',border:'1px solid var(--hairline)',borderRadius:'12px',
                      padding:'16px 20px',
                    }}>
                      <div style={{display:'flex',alignItems:'center',gap:'14px',flexWrap:'wrap'}}>
                        <div style={{fontSize:'24px',flexShrink:0}}>
                          {game.sport === 'baseball' ? '⚾' : game.sport === 'softball' ? '🥎' : game.sport === 'basketball' ? '🏀' : '⚽'}
                        </div>
                        <div style={{flex:1,minWidth:'180px'}}>
                          <div style={{fontWeight:700,fontSize:'15px'}}>{game.title}</div>
                          <div style={{fontSize:'13px',color:'var(--ink-3)',marginTop:'2px'}}>
                            {formatDate(game.scheduled_at)}{game.location ? ` · ${game.location}` : ''}
                          </div>
                          <div style={{fontSize:'12px',color:'var(--ink-4)',marginTop:'2px'}}>
                            {game.away_team} @ {game.home_team}
                          </div>
                        </div>
                        <div style={{display:'flex',gap:'8px',flexWrap:'wrap',alignItems:'center'}}>
                          <span style={{
                            fontSize:'11px',fontWeight:700,padding:'3px 10px',borderRadius:'20px',
                            textTransform:'uppercase',letterSpacing:'0.5px',
                            ...(game.status === 'live'
                              ? { background: 'rgba(244,67,54,0.12)', color: '#F44336', border: '1px solid rgba(244,67,54,0.25)' }
                              : game.status === 'completed'
                              ? { background: 'rgba(34,197,94,0.1)', color: '#22C55E', border: '1px solid rgba(34,197,94,0.2)' }
                              : { background: 'rgba(74,85,104,0.15)', color: 'var(--ink-3)', border: '1px solid var(--hairline)' })
                            }}>
                            {game.status}
                          </span>
                          <button
                            className="btn btn-sm"
                            style={{ background: 'rgba(249,115,22,0.1)', color: '#F97316', border: '1px solid rgba(249,115,22,0.3)' }}
                            onClick={() => setExpandedStream(expandedStream === game.id ? null : game.id)}
                          >
                            📡 Camera Info
                          </button>
                          {game.status === 'completed' && (
                            <button
                              className="btn btn-sm"
                              style={{ background: 'rgba(249,115,22,0.1)', color: '#F97316', border: '1px solid rgba(249,115,22,0.3)' }}
                              onClick={() => setClipperGame(game)}
                            >
                              🎬 Highlight
                            </button>
                          )}
                          <button
                            className="btn btn-sm"
                            style={{ background: 'rgba(34,197,94,0.1)', color: '#22C55E', border: '1px solid rgba(34,197,94,0.3)' }}
                            onClick={() => setGameModal({ open: true, game })}
                          >
                            ✏️ Edit
                          </button>
                          <button className="btn btn-danger btn-sm" onClick={() => deleteGame(game.id)}>
                            Delete
                          </button>
                        </div>
                      </div>

                      {/* Expandable stream info */}
                      {expandedStream === game.id && game.stream_key && (
                        <StreamInfo streamKey={game.stream_key} />
                      )}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ── Tab: Users ── */}
      {activeTab === 'users' && (
        <div>
          <div style={{ marginBottom: '20px' }}>
            <p style={{color:'var(--ink-3)',fontSize:'13px'}}>
              Pending Coach and Player/Parent accounts that selected your team at registration.
            </p>
          </div>

          {pendingUsers.length === 0 ? (
            <div style={{textAlign:'center',padding:'60px',background:'var(--surface)',border:'1px solid var(--hairline)',borderRadius:'16px'}}>
              <div style={{fontSize:'40px',marginBottom:'12px'}}>✅</div>
              <p style={{color:'var(--ink-3)'}}>No pending approvals for your team.</p>
            </div>
          ) : (
            <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
              {pendingUsers.map((u: any) => {
                const isPlayerParent = u.requested_role === 'player_parent';
                const teamPlayers = allPlayers.filter(p => managedTeamIds.length === 0 || teams.some(t => t.id === p.team_id));
                const canApprove = !isPlayerParent || approvePlayerLinks[u.id];

                return (
                  <div key={u.id} style={{
                    background:'var(--surface)',border:'1px solid var(--hairline)',borderRadius:'12px',
                    padding:'16px 20px',display:'flex',alignItems:'flex-start',gap:'14px',flexWrap:'wrap',
                  }}>
                    {/* Avatar */}
                    <div style={{
                      width:40,height:40,borderRadius:'50%',flexShrink:0,
                      background:'linear-gradient(135deg, #F97316, #EA580C)',
                      display:'flex',alignItems:'center',justifyContent:'center',
                      fontSize:'16px',fontWeight:800,color:'#fff',
                    }}>
                      {u.username.charAt(0).toUpperCase()}
                    </div>

                    <div style={{flex:1,minWidth:'160px'}}>
                      <div style={{display:'flex',alignItems:'center',gap:'8px',flexWrap:'wrap'}}>
                        <span style={{fontWeight:800,fontSize:'15px'}}>{u.username}</span>
                        <span style={{
                          fontSize: '10px', fontWeight: 700, letterSpacing: '0.8px', textTransform: 'uppercase',
                          padding: '2px 8px', borderRadius: '20px',
                          background: isPlayerParent ? 'rgba(139,92,246,0.12)' : 'rgba(6,182,212,0.12)',
                          color: isPlayerParent ? '#A78BFA' : '#22D3EE',
                          border: `1px solid ${isPlayerParent ? 'rgba(139,92,246,0.3)' : 'rgba(6,182,212,0.3)'}`,
                        }}>
                          {isPlayerParent ? 'Player/Parent' : 'Coach'}
                        </span>
                      </div>
                      <div style={{fontSize:'13px',color:'var(--ink-3)',marginTop:'2px'}}>{u.email}</div>
                      <div style={{fontSize:'11px',color:'var(--ink-4)',marginTop:'3px'}}>
                        Registered {new Date(u.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </div>

                      {isPlayerParent && (
                        <div style={{marginTop:'10px',maxWidth:'260px'}}>
                          <label style={{
                            fontSize:'11px',color:'var(--ink-3)',fontWeight:700,
                            textTransform:'uppercase',letterSpacing:'0.5px',display:'block',marginBottom:'5px',
                          }}>
                            Link to Player *
                          </label>
                          <select
                            className="form-control"
                            value={approvePlayerLinks[u.id] || ''}
                            onChange={e => setApprovePlayerLinks(prev => ({ ...prev, [u.id]: e.target.value }))}
                            style={{ fontSize: '13px', padding: '7px 10px' }}
                          >
                            <option value="">— Select player —</option>
                            {teamPlayers.map(p => (
                              <option key={p.id} value={p.id}>
                                {p.name}{p.number ? ` #${p.number}` : ''}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}
                    </div>

                    {/* Approve / Reject */}
                    <div style={{ display: 'flex', gap: '8px', flexShrink: 0, alignSelf: 'center' }}>
                      <button
                        className="btn btn-sm"
                        style={{
                          background: canApprove ? 'rgba(34,197,94,0.12)' : 'rgba(34,197,94,0.05)',
                          color:canApprove?'#22C55E':'var(--ink-4)',
                          border:`1px solid ${canApprove?'rgba(34,197,94,0.3)':'var(--hairline)'}`,
                          cursor:canApprove?'pointer':'not-allowed',
                        }}
                        disabled={!canApprove}
                        onClick={() => handleApprovePendingUser(u.id)}
                      >
                        ✓ Approve
                      </button>
                      <button className="btn btn-danger btn-sm" onClick={() => handleRejectPendingUser(u.id)}>
                        ✕ Reject
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ── Modals ── */}
      {clipperGame && (
        <GameFilmAndClipModal
          game={clipperGame}
          onClose={() => setClipperGame(null)}
        />
      )}

      {teamModal.open && (
        <TeamModal
          team={teamModal.team}
          onClose={() => setTeamModal({ open: false })}
          onSaved={async (t) => {
            await fetchMyTeams();
            if (!teamModal.team) setSelectedTeamId(t.id);
            showToast(teamModal.team ? 'Team updated' : 'Team created');
          }}
        />
      )}

      {rosterScanOpen && selectedTeamId && (
        <RosterScanModal
          teamId={selectedTeamId}
          onClose={() => setRosterScanOpen(false)}
          onImported={async () => { await fetchPlayers(); showToast('Roster imported!'); }}
        />
      )}

      {playerModal.open && selectedTeamId && (
        <PlayerModal
          player={playerModal.player}
          teamId={selectedTeamId}
          onClose={() => setPlayerModal({ open: false })}
          onSaved={async () => {
            await fetchPlayers();
            showToast(playerModal.player ? 'Player updated' : 'Player added');
          }}
        />
      )}

      {gameModal.open && selectedTeamId && selectedTeam && (
        <GameModal
          game={gameModal.game}
          teamId={selectedTeamId}
          teamName={selectedTeam.name}
          onClose={() => setGameModal({ open: false })}
          onSaved={async () => {
            await fetchGames();
            showToast(gameModal.game ? 'Game updated' : 'Game created');
          }}
        />
      )}

      {/* ── Toast ── */}
      {toast && (
        <div style={{
          position: 'fixed', bottom: '90px', left: '50%', transform: 'translateX(-50%)',
          background: '#22C55E', color: '#fff', padding: '10px 20px', borderRadius: '8px',
          fontWeight: 700, fontSize: '14px', zIndex: 9999,
          boxShadow: '0 8px 24px rgba(0,0,0,0.5)', pointerEvents: 'none', whiteSpace: 'nowrap',
        }}>
          ✓ {toast}
        </div>
      )}
    </div>
  );
};

export default ManagerPortal;
